import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import {
  MarketplaceContract,
  MarketplaceContractCreate,
  MarketplaceContractDetail,
  MarketplaceContractFilter,
  MarketplaceContractPaymentCreate,
  MarketplaceContractUpdate,
} from '@core/types/marketplace-contract.type';

@Injectable({ providedIn: 'root' })
export class MarketplaceContractService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/contracts`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<MarketplaceContract>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as MarketplaceContractFilter;
    if (filter.supplierId) params = params.set('filter.supplierId', filter.supplierId);
    if (filter.status !== undefined) params = params.set('filter.status', String(filter.status));

    return this.http.get<ApiResponse<PaginationResult<MarketplaceContract>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading contracts')),
    );
  }

  get(id: string): Observable<ApiResponse<MarketplaceContractDetail>> {
    return this.http.get<ApiResponse<MarketplaceContractDetail>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading contract')),
    );
  }

  create(data: MarketplaceContractCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating contract')),
    );
  }

  update(data: MarketplaceContractUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating contract')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting contract')),
    );
  }

  addPayment(contractId: string, data: MarketplaceContractPaymentCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/${contractId}/payments`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error adding payment')),
    );
  }

  deletePayment(contractId: string, paymentId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${contractId}/payments/${paymentId}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting payment')),
    );
  }
}

